import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_CONFIG } from '@/config/api.config';

/**
 * How many docket entries are outstanding, for the header badge.
 *
 * Deliberately standalone rather than importing from `src/docket/`: the header is in
 * the main bundle, and pulling the docket's api module in would start dragging that
 * code to every user whether or not they open the docket.
 *
 * Fails silently. A user with no docket, an older backend, or a network blip should
 * see no badge — never an error in the middle of the app chrome.
 */
export function useDocketCount(pollMs = 5 * 60 * 1000) {
  const [open, setOpen] = useState(0);
  const [overdue, setOverdue] = useState(0);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('id_token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_CONFIG.baseUrl}/docket/tasks`, {
        // A year out: far enough to mean "everything on my plate", near enough that
        // a maintenance date a decade away doesn't inflate the badge.
        params: { days: 365 },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setOpen((data?.tasks || []).length);
      setOverdue((data?.overdue || []).length);
    } catch {
      // Silent by design — see above.
    }
  }, []);

  useEffect(() => {
    refresh();

    const onFocus = () => refresh();
    // The docket dispatches this after a change so the badge doesn't lag behind.
    const onChanged = () => refresh();

    window.addEventListener('focus', onFocus);
    window.addEventListener('docket-updated', onChanged);
    const timer = window.setInterval(refresh, pollMs);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('docket-updated', onChanged);
      window.clearInterval(timer);
    };
  }, [refresh, pollMs]);

  return { open, overdue, refresh };
}
